import type { CompiledSurvey, SurveyRuntimeContext } from '@apruv/survey-contract/compiler';
import type { CXPublicSurveyResponse } from '../../services/cxPublicSurveyService';
import type { FlowValues } from '../types';
import { canonicalComponentConfig } from './surveyPresentation';
import { canonicalSurveyFlags } from './canonicalSurveyFlags';
import { canonicalPreviewContext } from '../preview/canonicalPreviewContext';

export function canonicalExecutionPolicy(response: CXPublicSurveyResponse) {
  const preview = canonicalPreviewContext(response);
  return Object.freeze({
    ...(preview ? { preview: true } : {}),
    persist: !preview && response.testMode !== true && response.persistenceDisabled !== true,
    external: !preview && response.testMode !== true,
    tip:
      response.testMode !== true &&
      response.tipsDisabled !== true &&
      response.runtimeContext?.capabilities.tippingConfigured === true &&
      response.runtimeContext?.capabilities.tipEligibility === 'server_check_required',
  });
}

export function canonicalSurveyContext(
  compiled: CompiledSurvey,
  response: CXPublicSurveyResponse
): SurveyRuntimeContext {
  const raw = response.sourceEvent?.payload?.raw as Record<string, unknown> | undefined;
  const serverMode = raw?.scoreMethod;
  const priorMode = response.existingResponse?.scoreMethod;
  const normalize = (mode: unknown) => {
    if (mode === undefined || mode === null) return undefined;
    if (typeof mode !== 'string' || !['nps', 'csat'].includes(mode.toLowerCase()))
      throw new Error('Unsupported survey rating method.');
    return mode.toLowerCase() as 'nps' | 'csat';
  };
  const selected = normalize(serverMode);
  const existing = normalize(priorMode);
  if (selected && existing && selected !== existing)
    throw new Error('The saved response and survey rating methods disagree.');
  const modes = compiled.snapshot.configuration.rating.allowedModes ?? [
    compiled.snapshot.configuration.rating.mode,
  ];
  const mode = existing ?? selected ?? (modes.length === 1 ? modes[0] : undefined);
  if (!mode || !modes.includes(mode))
    throw new Error('A supported rating method must be selected for this survey.');
  const resolvedFlags = canonicalSurveyFlags(compiled, response);
  const policy = canonicalExecutionPolicy(response);
  return Object.freeze({
    resolvedFlags,
    score_method: mode,
    ...(response.existingResponse ? { score: response.existingResponse.rating } : {}),
    has_existing_response: response.existingResponse?.submitted === true,
    response_editing_enabled:
      compiled.snapshot.configuration.survey?.responseEditingEnabled ?? true,
    rating_enabled: compiled.snapshot.configuration.survey?.ratingEnabled ?? true,
    // Test links can show the configured tip step; execution remains disabled by policy.tip.
    tipping_enabled: (policy.tip || response.testMode === true) && compiled.snapshot.configuration.tipping.enabled,
    has_service_proof: (response.runtimeContext?.serviceMedia.length ?? 0) > 0,
    has_technician_notes: Boolean(response.runtimeContext?.technicianNotes),
    service_type: response.sourceEvent?.serviceType ?? '',
    channel: 'portal',
    locale:
      canonicalPreviewContext(response)?.locale ??
      compiled.snapshot.configuration.localization.defaultLocale,
  });
}

/** Only the identified rating answer contributes a score; wrong-mode/stale answers cannot win by order. */
export function canonicalAnswerContext(
  base: SurveyRuntimeContext,
  values: FlowValues,
  ratingComponentId: string | undefined,
  compiled: CompiledSurvey
): SurveyRuntimeContext {
  const rating = ratingComponentId
    ? (values[ratingComponentId] as { score?: unknown; sentiment?: unknown } | undefined)
    : undefined;
  const reserved = new Set([
    'appointmentCode',
    'cxExecutionStepId',
    'cxSurveyCode',
    'cxTriggerGroupId',
  ]);
  const answerIds = new Set<string>();
  for (const screen of compiled.snapshot.screens)
    for (const component of screen.components) {
      answerIds.add(component.id);
      const config = canonicalComponentConfig(compiled, component, base);
      if (typeof config?.routeValueKey === 'string') answerIds.add(config.routeValueKey);
    }
  const comments: string[] = [];
  const tags: string[] = [];
  const options: string[] = [];
  for (const [id, value] of Object.entries(values)) {
    if (!answerIds.has(id) || reserved.has(id)) continue;
    if (typeof value === 'string') {
      options.push(value);
      continue;
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const answer = value as {
      comment?: string;
      text?: string;
      notes?: string;
      tags?: string[];
      selectedOption?: string;
      feedback?: { notes?: string; tags?: string[] };
    };
    for (const text of [answer.comment, answer.text, answer.notes, answer.feedback?.notes])
      if (typeof text === 'string') comments.push(text);
    const answerTags = answer.tags ?? answer.feedback?.tags;
    if (Array.isArray(answerTags))
      for (const tag of answerTags) if (typeof tag === 'string') tags.push(tag);
    if (typeof answer.selectedOption === 'string') options.push(answer.selectedOption);
  }
  return {
    ...base,
    ...(ratingComponentId ? { score: rating?.score ?? undefined } : {}),
    ...(typeof rating?.sentiment === 'string' ? { sentiment: rating.sentiment } : {}),
    comment: comments.join('\n'),
    tag: tags,
    selected_option: options,
  } as SurveyRuntimeContext;
}
